import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MousePointer2, Car, Bike, Truck, PersonStanding, ArrowUpRight, Pencil, Minus, Type, Undo2, Trash2, RotateCw, Satellite, Loader2, Square, ChevronDown, ChevronUp } from "lucide-react";
import { Button, Input, Select } from "@/components/ui";
import { tileFor, SATELLITE_TILE_URL } from "@/features/sitemaps/siteMapThumb";

// =============================================================================
// AccidentSketch — the mud map of a vehicle accident, drawn on the phone
// (mig 482 piece 2, 1 Nov 2026)
// =============================================================================
// James, 1 Nov 2026: "I won't accept any incident without the scribe / draw it
// down as the insurance need it" — and, after the first cut: "make it more
// adaptable and user workable: select lanes, road map type, typical accident
// maps plus a free draw with straight snap lines, line type and drop in cars
// and accident areas."
//
// So the road is BUILT, not picked from a fixed list:
//   road type   straight · cross intersection · T-junction · roundabout · blank
//   lanes       ours 1–4, oncoming 1–4 (roundabout 1–2)
//   extras      right-turn pocket beside the median, a cross street at the top
// and a "typical accident" list sets the road AND drops the vehicles, arrows
// and impact in place for the common ones (rear-end at a turn lane — Larry's,
// rear-end at the lights, side impact at an intersection, roundabout merge,
// lane change, reversing) so the driver moves things rather than starts from
// nothing.
//
// Tools: our vehicle / other vehicle / truck / bike / pedestrian / impact
// stamps (drag to move, rotate 45°), direction arrows, a straight LINE that
// snaps to 0/45/90° with a line type (lane line, edge line, centre line, kerb,
// skid mark), free pen, an AREA box for the debris / impact zone, and labels.
// Satellite tiles sit under the drawing when the phone has a GPS fix at the
// scene (the site-map tiles, stitched 3×3 — no Leaflet).
//
// Everything drawn is a list in React state and repainted; Undo pops it. The
// caller exports a PNG with a legend when the person moves on (exportBlob on
// the handle) and uploads it to hse-evidence beside the photos — the broker
// report prints it. Australian geometry: we drive up the LEFT half; the
// right-turn pocket sits beside the median. Facts only — nothing here says
// whose fault it was.
// =============================================================================

export type SketchStamp = "coretel" | "other" | "truck" | "bike" | "pedestrian" | "impact";
export type LineType = "dash" | "solid" | "kerb" | "double" | "skid";
export type SketchItem =
  | { kind: "stamp"; stamp: SketchStamp; x: number; y: number; rot: number }
  | { kind: "arrow"; x1: number; y1: number; x2: number; y2: number }
  | { kind: "line"; x1: number; y1: number; x2: number; y2: number; type: LineType }
  | { kind: "pen"; pts: Array<[number, number]> }
  | { kind: "area"; x: number; y: number; w: number; h: number }
  | { kind: "text"; x: number; y: number; text: string };
type Mode = "select" | SketchStamp | "arrow" | "line" | "pen" | "area" | "text";

export type RoadKind = "straight" | "cross" | "tee" | "roundabout" | "blank";
export interface RoadSpec { kind: RoadKind; lanesOurs: number; lanesOncoming: number; turnPocket: boolean; leftTurn?: boolean; crossStreet: boolean }   // turnPocket = right-turn lane beside the median; leftTurn = left-turn lane on the kerb side (James, 1 Nov)
export const DEFAULT_ROAD: RoadSpec = { kind: "straight", lanesOurs: 2, lanesOncoming: 2, turnPocket: false, crossStreet: false };

export const W = 1000, H = 700;    // logical canvas — the PNG is this size
const LANE = 100;
const ROAD_KINDS: Array<[RoadKind, string]> = [["straight", "Straight road"], ["cross", "Intersection"], ["tee", "T-junction"], ["roundabout", "Roundabout"], ["blank", "Blank"]];
const LINE_TYPES: Array<[LineType, string]> = [["dash", "Lane line"], ["solid", "Edge line"], ["double", "Centre line"], ["kerb", "Kerb"], ["skid", "Skid mark"]];
const STAMP_LABEL: Record<SketchStamp, string> = { coretel: "Our vehicle", other: "Other vehicle", truck: "Truck", bike: "Motorbike", pedestrian: "Pedestrian", impact: "Impact" };
const ORANGE = "#ff6a1a", BLUE = "#2563eb", RED = "#e0323a";

// Typical accidents — the road, plus the vehicles already in place to be moved.
interface Preset { key: string; label: string; road: RoadSpec; items: SketchItem[] }
const PRESETS: Preset[] = [
  { key: "rear_turnlane", label: "Rear-ended in a turn lane", road: { kind: "straight", lanesOurs: 3, lanesOncoming: 2, turnPocket: true, crossStreet: true },
    items: [{ kind: "stamp", stamp: "coretel", x: 482, y: 215, rot: 0 }, { kind: "stamp", stamp: "other", x: 482, y: 350, rot: 0 }, { kind: "stamp", stamp: "impact", x: 482, y: 275, rot: 0 }, { kind: "arrow", x1: 482, y1: 560, x2: 482, y2: 420 }] },
  { key: "rear_lights", label: "Rear-ended at the lights", road: { kind: "straight", lanesOurs: 2, lanesOncoming: 2, turnPocket: false, crossStreet: true },
    items: [{ kind: "stamp", stamp: "coretel", x: 350, y: 215, rot: 0 }, { kind: "stamp", stamp: "other", x: 350, y: 350, rot: 0 }, { kind: "stamp", stamp: "impact", x: 350, y: 275, rot: 0 }, { kind: "arrow", x1: 350, y1: 560, x2: 350, y2: 420 }] },
  { key: "side_cross", label: "Side impact at an intersection", road: { kind: "cross", lanesOurs: 2, lanesOncoming: 2, turnPocket: false, crossStreet: false },
    items: [{ kind: "stamp", stamp: "coretel", x: 450, y: 420, rot: 0 }, { kind: "stamp", stamp: "other", x: 640, y: 300, rot: 270 }, { kind: "stamp", stamp: "impact", x: 495, y: 335, rot: 0 }, { kind: "arrow", x1: 450, y1: 640, x2: 450, y2: 490 }, { kind: "arrow", x1: 880, y1: 300, x2: 720, y2: 300 }] },
  { key: "roundabout", label: "Hit entering a roundabout", road: { kind: "roundabout", lanesOurs: 1, lanesOncoming: 1, turnPocket: false, crossStreet: false },
    items: [{ kind: "stamp", stamp: "coretel", x: 455, y: 580, rot: 0 }, { kind: "stamp", stamp: "other", x: 640, y: 440, rot: 315 }, { kind: "stamp", stamp: "impact", x: 560, y: 515, rot: 0 }, { kind: "arrow", x1: 455, y1: 690, x2: 455, y2: 640 }] },
  { key: "lane_change", label: "Lane change - side swipe", road: { kind: "straight", lanesOurs: 3, lanesOncoming: 2, turnPocket: false, crossStreet: false },
    items: [{ kind: "stamp", stamp: "coretel", x: 300, y: 330, rot: 20 }, { kind: "stamp", stamp: "other", x: 400, y: 300, rot: 0 }, { kind: "stamp", stamp: "impact", x: 352, y: 300, rot: 0 }, { kind: "arrow", x1: 300, y1: 560, x2: 300, y2: 420 }, { kind: "arrow", x1: 400, y1: 560, x2: 400, y2: 420 }] },
  { key: "reversing", label: "Reversing / car park", road: { kind: "blank", lanesOurs: 2, lanesOncoming: 2, turnPocket: false, crossStreet: false },
    items: [{ kind: "stamp", stamp: "coretel", x: 450, y: 350, rot: 180 }, { kind: "stamp", stamp: "other", x: 560, y: 350, rot: 0 }, { kind: "stamp", stamp: "impact", x: 505, y: 350, rot: 0 }] },
];

export interface AccidentSketchHandle {
  exportBlob: () => Promise<Blob | null>;
  isEmpty: () => boolean;
}

export function AccidentSketch({ items, onChange, road, onRoad, gps, handleRef }: {
  items: SketchItem[];
  onChange: (items: SketchItem[]) => void;
  road: RoadSpec;
  onRoad: (r: RoadSpec) => void;
  gps: { latitude: number; longitude: number } | null;
  handleRef: React.MutableRefObject<AccidentSketchHandle | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<Mode>("coretel");
  const [lineType, setLineType] = useState<LineType>("dash");
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<SketchItem | null>(null);      // the arrow / line / pen / area being drawn
  const dragRef = useRef<{ idx: number; dx: number; dy: number } | null>(null);
  const [textAt, setTextAt] = useState<{ x: number; y: number } | null>(null);
  const [textVal, setTextVal] = useState("");
  const [roadOpen, setRoadOpen] = useState(true);
  const [satellite, setSatellite] = useState(false);
  const [satBusy, setSatBusy] = useState(false);
  const [satErr, setSatErr] = useState<string | null>(null);
  const satCanvas = useRef<HTMLCanvasElement | null>(null);
  const [satZoom, setSatZoom] = useState(18);

  // ---- satellite under the drawing (only with a GPS fix, only online) -------
  useEffect(() => {
    if (!satellite || !gps) { satCanvas.current = null; return; }
    let cancelled = false;
    setSatBusy(true); setSatErr(null);
    (async () => {
      try {
        const c = tileFor(gps.latitude, gps.longitude, satZoom);
        const cv = document.createElement("canvas"); cv.width = 768; cv.height = 768;
        const g = cv.getContext("2d")!;
        const loads: Promise<void>[] = [];
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const url = SATELLITE_TILE_URL.replace("{z}", String(c.z)).replace("{y}", String(c.y + dy)).replace("{x}", String(c.x + dx));
          loads.push(new Promise<void>((res) => {
            const im = new Image(); im.crossOrigin = "anonymous";
            im.onload = () => { g.drawImage(im, (dx + 1) * 256, (dy + 1) * 256); res(); };
            im.onerror = () => res();     // a missing tile just leaves a gap
            im.src = url;
          }));
        }
        await Promise.all(loads);
        if (cancelled) return;
        const out = document.createElement("canvas"); out.width = W; out.height = H;
        const og = out.getContext("2d")!;
        const scale = Math.max(W / 768, H / 768);
        const sw = W / scale, sh = H / scale;
        og.drawImage(cv, (768 - sw) / 2, (768 - sh) / 2, sw, sh, 0, 0, W, H);
        satCanvas.current = out;
      } catch { if (!cancelled) setSatErr("Couldn't load the satellite image — the drawn road still works."); }
      finally { if (!cancelled) setSatBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [satellite, gps?.latitude, gps?.longitude, satZoom]);

  // ---- painting --------------------------------------------------------------
  const paint = useCallback((g: CanvasRenderingContext2D, forExport = false) => {
    g.clearRect(0, 0, W, H);
    if (satellite && satCanvas.current) { g.drawImage(satCanvas.current, 0, 0); g.fillStyle = "rgba(255,255,255,0.12)"; g.fillRect(0, 0, W, H); }
    else drawRoad(g, road);
    const all = draft ? [...items, draft] : items;
    const dark = road.kind === "blank" && !(satellite && satCanvas.current);   // white markings vanish on the white grid
    all.forEach((it, i) => drawItem(g, it, !forExport && i === selected, dark));
    if (forExport) drawLegend(g, items);
  }, [items, draft, selected, road, satellite]);

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    paint(cv.getContext("2d")!);
  }, [paint, satBusy]);

  useEffect(() => {
    handleRef.current = {
      isEmpty: () => items.length === 0,
      exportBlob: async () => {
        const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
        paint(cv.getContext("2d")!, true);
        return new Promise<Blob | null>(res => cv.toBlob(b => res(b), "image/png"));
      },
    };
  }, [paint, items.length, handleRef]);

  // ---- pointer handling ------------------------------------------------------
  const toLogical = (e: React.PointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  function onDown(e: React.PointerEvent) {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const { x, y } = toLogical(e);
    if (mode === "coretel" || mode === "other" || mode === "truck" || mode === "bike" || mode === "pedestrian" || mode === "impact") {
      onChange([...items, { kind: "stamp", stamp: mode, x, y, rot: 0 }]);
      setSelected(items.length); setMode("select");
      return;
    }
    if (mode === "arrow") { setDraft({ kind: "arrow", x1: x, y1: y, x2: x, y2: y }); return; }
    if (mode === "line")  { setDraft({ kind: "line", x1: x, y1: y, x2: x, y2: y, type: lineType }); return; }
    if (mode === "pen")   { setDraft({ kind: "pen", pts: [[x, y]] }); return; }
    if (mode === "area")  { setDraft({ kind: "area", x, y, w: 0, h: 0 }); return; }
    if (mode === "text")  { setTextAt({ x, y }); setTextVal(""); return; }
    const idx = hitTest(items, x, y);
    setSelected(idx);
    if (idx != null) {
      const [ox, oy] = origin(items[idx]);
      dragRef.current = { idx, dx: x - ox, dy: y - oy };
    }
  }
  function onMove(e: React.PointerEvent) {
    if (!draft && !dragRef.current) return;
    e.preventDefault();
    const { x, y } = toLogical(e);
    if (draft?.kind === "arrow") setDraft({ ...draft, x2: x, y2: y });
    else if (draft?.kind === "line") { const s = snap(draft.x1, draft.y1, x, y); setDraft({ ...draft, x2: s.x, y2: s.y }); }
    else if (draft?.kind === "pen") setDraft({ ...draft, pts: [...draft.pts, [x, y]] });
    else if (draft?.kind === "area") setDraft({ ...draft, w: x - draft.x, h: y - draft.y });
    else if (dragRef.current) {
      const { idx, dx, dy } = dragRef.current;
      onChange(items.map((it, i) => i === idx ? moveTo(it, x - dx, y - dy) : it));
    }
  }
  function onUp() {
    if (draft) {
      let ok = true;
      if (draft.kind === "arrow" || draft.kind === "line") ok = Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) > 12;
      if (draft.kind === "pen") ok = draft.pts.length > 2;
      if (draft.kind === "area") ok = Math.abs(draft.w) > 16 && Math.abs(draft.h) > 16;
      if (ok) onChange([...items, draft.kind === "area" ? normArea(draft) : draft]);
      setDraft(null);
    }
    dragRef.current = null;
  }
  function addText() {
    if (!textAt || !textVal.trim()) { setTextAt(null); return; }
    onChange([...items, { kind: "text", x: textAt.x, y: textAt.y, text: textVal.trim() }]);
    setTextAt(null); setTextVal(""); setMode("select");
  }
  function applyPreset(key: string) {
    const p = PRESETS.find(x => x.key === key); if (!p) return;
    onRoad({ ...p.road }); onChange(p.items.map(i => ({ ...i })));
    setSelected(null); setMode("select"); setSatellite(false); setRoadOpen(false);
  }
  const sel = selected != null ? items[selected] : null;
  const setRoad = (patch: Partial<RoadSpec>) => { onRoad({ ...road, ...patch }); setSatellite(false); };

  const tools: Array<{ m: Mode; label: string; icon: any; colour?: string }> = useMemo(() => [
    { m: "coretel",    label: "Our vehicle", icon: Car, colour: ORANGE },
    { m: "other",      label: "Other vehicle", icon: Car, colour: BLUE },
    { m: "truck",      label: "Truck", icon: Truck, colour: BLUE },
    { m: "bike",       label: "Bike", icon: Bike, colour: BLUE },
    { m: "pedestrian", label: "Person", icon: PersonStanding },
    { m: "impact",     label: "Impact", icon: Square, colour: RED },
    { m: "area",       label: "Area", icon: Square, colour: RED },
    { m: "arrow",      label: "Direction", icon: ArrowUpRight },
    { m: "line",       label: "Line", icon: Minus },
    { m: "pen",        label: "Draw", icon: Pencil },
    { m: "text",       label: "Label", icon: Type },
    { m: "select",     label: "Move", icon: MousePointer2 },
  ], []);
  const hint = mode === "select" ? "Tap a thing to move it; Rotate turns a vehicle"
    : mode === "arrow" ? "Drag the way the vehicle was going"
    : mode === "line" ? "Drag a line — it snaps straight"
    : mode === "pen" ? "Draw freehand"
    : mode === "area" ? "Drag a box over the debris / impact area"
    : mode === "text" ? "Tap where the label goes"
    : `Tap where the ${STAMP_LABEL[mode as SketchStamp].toLowerCase()} was`;

  return (
    <div className="space-y-2.5">
      {/* ---- the road ---- */}
      <div className="rounded-md border border-line bg-ink p-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <Select className="flex-1" value="" onChange={e => { if (e.target.value) applyPreset(e.target.value); }}>
            <option value="">Start from a typical accident…</option>
            {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </Select>
          <button type="button" onClick={() => setRoadOpen(o => !o)} className="text-[11px] text-text-muted inline-flex items-center gap-1 px-2 py-1.5 border border-line rounded">
            Road {roadOpen ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          </button>
        </div>
        {roadOpen && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {ROAD_KINDS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setRoad({ kind: k })}
                  className={`text-[11px] px-2.5 py-1.5 rounded border transition ${!satellite && road.kind === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>{l}</button>
              ))}
              <button type="button" disabled={!gps} onClick={() => setSatellite(s => !s)} title={gps ? "Satellite under the drawing, at your GPS" : "Needs a GPS fix — you have to be at the scene"}
                className={`text-[11px] px-2.5 py-1.5 rounded border transition inline-flex items-center gap-1 disabled:opacity-40 ${satellite ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                {satBusy ? <Loader2 size={11} className="animate-spin"/> : <Satellite size={11}/>} Satellite (my GPS)
              </button>
              {satellite && (
                <select value={satZoom} onChange={e => setSatZoom(Number(e.target.value))} className="text-[11px] bg-ink border border-line rounded px-1.5 text-text-muted">
                  <option value={17}>wide</option><option value={18}>normal</option><option value={19}>close</option>
                </select>
              )}
            </div>
            {road.kind !== "blank" && !satellite && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-text-muted">
                <LaneCount label={road.kind === "roundabout" ? "Lanes" : "Lanes our way"} value={road.lanesOurs} max={road.kind === "roundabout" ? 2 : 4} onChange={v => setRoad({ lanesOurs: v, ...(road.kind === "roundabout" ? { lanesOncoming: v } : {}) })}/>
                {road.kind !== "roundabout" && <LaneCount label="Lanes oncoming" value={road.lanesOncoming} max={4} onChange={v => setRoad({ lanesOncoming: v })}/>}
                {road.kind === "straight" && (
                  <>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={road.turnPocket} onChange={e => setRoad({ turnPocket: e.target.checked })}/> Right-turn lane</label>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!road.leftTurn} onChange={e => setRoad({ leftTurn: e.target.checked })}/> Left-turn lane</label>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={road.crossStreet} onChange={e => setRoad({ crossStreet: e.target.checked })}/> Cross street at the top</label>
                  </>
                )}
              </div>
            )}
            {satErr && <div className="text-[11px] text-warn">{satErr}</div>}
          </>
        )}
      </div>

      {/* ---- tools ---- */}
      <div className="flex flex-wrap gap-1.5">
        {tools.map(t => {
          const Icon = t.icon; const on = mode === t.m;
          return (
            <button key={t.m} type="button" onClick={() => { setMode(t.m); if (t.m !== "select") setSelected(null); }}
              className={`text-[11px] px-2 py-1.5 rounded border transition inline-flex items-center gap-1 ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
              <Icon size={12} style={t.colour && !on ? { color: t.colour } : undefined}/> {t.label}
            </button>
          );
        })}
      </div>
      {mode === "line" && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-text-muted">
          <span>Line type:</span>
          {LINE_TYPES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setLineType(k)}
              className={`px-2 py-1 rounded border transition ${lineType === k ? "bg-accent/15 border-accent text-accent" : "border-line hover:text-text"}`}>{l}</button>
          ))}
        </div>
      )}

      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          className="w-full rounded-md border border-line bg-white select-none" style={{ touchAction: "none", aspectRatio: `${W} / ${H}` }}/>
        {textAt && (
          <div className="absolute left-2 right-2 bottom-2 flex gap-2 bg-ink-raised/95 border border-line rounded-md p-2">
            <Input autoFocus value={textVal} onChange={e => setTextVal(e.target.value)} placeholder="Label, e.g. Gympie Rd northbound" onKeyDown={e => { if (e.key === "Enter") addText(); }}/>
            <Button size="sm" onClick={addText}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setTextAt(null)}>Cancel</Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Button size="sm" variant="secondary" icon={Undo2} disabled={items.length === 0} onClick={() => { onChange(items.slice(0, -1)); setSelected(null); }}>Undo</Button>
        {sel?.kind === "stamp" && (
          <Button size="sm" variant="secondary" icon={RotateCw} onClick={() => onChange(items.map((it, i) => i === selected && it.kind === "stamp" ? { ...it, rot: (it.rot + 45) % 360 } : it))}>Rotate</Button>
        )}
        {sel && (
          <Button size="sm" variant="secondary" icon={Trash2} onClick={() => { onChange(items.filter((_, i) => i !== selected)); setSelected(null); }}>Remove</Button>
        )}
        <Button size="sm" variant="ghost" disabled={items.length === 0} onClick={() => { onChange([]); setSelected(null); }}>Clear all</Button>
        <span className="text-[11px] text-text-muted ml-auto">{hint}</span>
      </div>
    </div>
  );
}

function LaneCount({ label, value, max, onChange }: { label: string; value: number; max: number; onChange: (v: number) => void }) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      {Array.from({ length: max }, (_, i) => i + 1).map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`w-6 h-6 rounded border text-[11px] ${value === n ? "bg-accent/15 border-accent text-accent" : "border-line hover:text-text"}`}>{n}</button>
      ))}
    </span>
  );
}

// -----------------------------------------------------------------------------
// drawing — a road diagram an insurer would recognise: asphalt, white
// markings, kerbs and verge. Australian geometry: we drive UP the page on the
// left half; the right-turn pocket sits beside the median.
// -----------------------------------------------------------------------------
const ASPHALT = "#4b4f54", MARK = "#f4f4f2", KERB = "#b9b4a6", VERGE = "#d9e3cc", MEDIAN = "#c9cfd6";

function line(g: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, width: number, colour: string, dashes: number[] = []) {
  g.save(); g.setLineDash(dashes); g.lineWidth = width; g.strokeStyle = colour; g.lineCap = "butt";
  g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.stroke(); g.restore();
}
const laneDash = (g: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) => line(g, x1, y1, x2, y2, 4, MARK, [30, 24]);
const edge     = (g: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) => line(g, x1, y1, x2, y2, 4, MARK);
function doubleCentre(g: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  const v = x1 === x2;
  line(g, x1 - (v ? 4 : 0), y1 - (v ? 0 : 4), x2 - (v ? 4 : 0), y2 - (v ? 0 : 4), 3, MARK);
  line(g, x1 + (v ? 4 : 0), y1 + (v ? 0 : 4), x2 + (v ? 4 : 0), y2 + (v ? 0 : 4), 3, MARK);
}
const kerbV = (g: CanvasRenderingContext2D, x: number, y1: number, y2: number) => line(g, x, y1, x, y2, 8, KERB);
const kerbH = (g: CanvasRenderingContext2D, y: number, x1: number, x2: number) => line(g, x1, y, x2, y, 8, KERB);
function roadArrow(g: CanvasRenderingContext2D, x: number, y: number, kind: "ahead" | "right" | "left") {
  g.save(); g.translate(x, y); if (kind === "left") { g.scale(-1, 1); kind = "right"; } g.fillStyle = MARK; g.strokeStyle = MARK; g.lineWidth = 9; g.lineCap = "butt";
  if (kind === "ahead") {
    g.beginPath(); g.moveTo(0, 40); g.lineTo(0, -20); g.stroke();
    g.beginPath(); g.moveTo(0, -44); g.lineTo(-16, -16); g.lineTo(16, -16); g.closePath(); g.fill();
  } else {
    g.beginPath(); g.moveTo(0, 40); g.lineTo(0, -6); g.lineTo(20, -6); g.stroke();
    g.beginPath(); g.moveTo(40, -6); g.lineTo(16, -22); g.lineTo(16, 10); g.closePath(); g.fill();
  }
  g.restore();
}
function chevrons(g: CanvasRenderingContext2D, x: number, w: number, y1: number, y2: number) {
  g.save(); g.strokeStyle = MARK; g.lineWidth = 3;
  for (let y = y1 + 20; y < y2; y += 34) { g.beginPath(); g.moveTo(x + 6, y); g.lineTo(x + w / 2, y + 16); g.lineTo(x + w - 6, y); g.stroke(); }
  g.restore();
}
/** Vertical two-way carriageway, our lanes on the left (heading up), oncoming on the right. No edge/kerb — the caller adds those. */
function lanesV(g: CanvasRenderingContext2D, x: number, yTop: number, yBot: number, ours: number, onc: number) {
  const w = (ours + onc) * LANE;
  g.fillStyle = ASPHALT; g.fillRect(x, yTop, w, yBot - yTop);
  for (let i = 1; i < ours; i++) laneDash(g, x + i * LANE, yTop, x + i * LANE, yBot);
  for (let i = 1; i < onc; i++) laneDash(g, x + (ours + i) * LANE, yTop, x + (ours + i) * LANE, yBot);
  doubleCentre(g, x + ours * LANE, yTop, x + ours * LANE, yBot);
}
/** Horizontal two-way carriageway: traffic heading RIGHT keeps left = the BOTTOM half; heading left uses the top. */
function lanesH(g: CanvasRenderingContext2D, y: number, xL: number, xR: number, ours: number, onc: number) {
  const w = (ours + onc) * LANE;
  g.fillStyle = ASPHALT; g.fillRect(xL, y, xR - xL, w);
  for (let i = 1; i < onc; i++) laneDash(g, xL, y + i * LANE, xR, y + i * LANE);
  for (let i = 1; i < ours; i++) laneDash(g, xL, y + (onc + i) * LANE, xR, y + (onc + i) * LANE);
  doubleCentre(g, xL, y + onc * LANE, xR, y + onc * LANE);
}

export function drawRoad(g: CanvasRenderingContext2D, r: RoadSpec) {
  g.save();
  g.fillStyle = VERGE; g.fillRect(0, 0, W, H);
  const ours = Math.max(1, Math.min(4, r.lanesOurs)), onc = Math.max(1, Math.min(4, r.lanesOncoming));
  if (r.kind === "straight") {
    const pocket = r.turnPocket ? LANE : 0, med = r.turnPocket ? 36 : 0, left = r.leftTurn ? LANE : 0;
    const w = (ours + onc) * LANE + pocket + med + left;
    const x0 = Math.round((W - w) / 2);      // kerb on our side
    const x = x0 + left;                      // where our through lanes start
    const top = r.crossStreet ? 120 : 0, stop = r.crossStreet ? 150 : null;
    if (r.crossStreet) { g.fillStyle = ASPHALT; g.fillRect(0, 0, W, 120); doubleCentre(g, 0, 60, W, 60); kerbH(g, 120, 0, x0); kerbH(g, 120, x0 + w, W); }
    g.fillStyle = ASPHALT; g.fillRect(x0, top, w, H - top);
    kerbV(g, x0, top, H); kerbV(g, x0 + w, top, H);
    edge(g, x0 + 8, stop ?? top, x0 + 8, H); edge(g, x0 + w - 8, top, x0 + w - 8, H);
    if (r.leftTurn) {
      // the left-turn pocket on the kerb side: opens at 330, chevrons below it
      const pocketStart = 330;
      edge(g, x, stop ?? top, x, pocketStart); laneDash(g, x, pocketStart, x, 470);
      g.fillStyle = MEDIAN; g.fillRect(x0 + 8, 470, left - 8, H - 470); chevrons(g, x0 + 8, left - 8, 470, H);
      edge(g, x, 470, x, H);
      roadArrow(g, x0 + left / 2, 400, "left"); roadArrow(g, x0 + left / 2, 250, "left");
    }
    for (let i = 1; i < ours; i++) laneDash(g, x + i * LANE, stop ?? top, x + i * LANE, H);
    const tx = x + ours * LANE;
    if (r.turnPocket) {
      const pocketStart = 330;
      edge(g, tx, stop ?? top, tx, pocketStart); laneDash(g, tx, pocketStart, tx, 470);
      g.fillStyle = MEDIAN; g.fillRect(tx, 470, pocket + med, H - 470); chevrons(g, tx, pocket + med, 470, H);
      edge(g, tx, 470, tx, H); edge(g, tx + pocket + med, 470, tx + pocket + med, H);
      g.fillStyle = MEDIAN; g.fillRect(tx + pocket, top, med, 470 - top);
      doubleCentre(g, tx + pocket + med / 2, top, tx + pocket + med / 2, 470);
      roadArrow(g, tx + pocket / 2, 400, "right"); roadArrow(g, tx + pocket / 2, 250, "right");
    } else {
      doubleCentre(g, tx, top, tx, H);
    }
    const ox = tx + pocket + med;
    for (let i = 1; i < onc; i++) laneDash(g, ox + i * LANE, top, ox + i * LANE, H);
    if (stop != null) line(g, x0 + 8, stop, tx + pocket - 4, stop, 8, MARK);
    for (let i = 0; i < ours; i++) roadArrow(g, x + (i + 0.5) * LANE, 560, "ahead");
  } else if (r.kind === "cross" || r.kind === "tee") {
    const wV = (ours + onc) * LANE, wH = wV;
    const cx = Math.round((W - wV) / 2), cy = Math.round((H - wH) / 2) - (r.kind === "tee" ? 70 : 0);
    lanesH(g, cy, 0, W, ours, onc);                                   // the cross road
    if (r.kind === "cross") lanesV(g, cx, 0, cy, ours, onc);          // the top arm
    lanesV(g, cx, cy + wH, H, ours, onc);                             // our arm, from the bottom
    g.fillStyle = ASPHALT; g.fillRect(cx, cy, wV, wH);                // the box, clean
    // kerbs and edge lines
    kerbH(g, cy, 0, cx); kerbH(g, cy, cx + wV, W); kerbH(g, cy + wH, 0, cx); kerbH(g, cy + wH, cx + wV, W);
    edge(g, 0, cy + 8, cx, cy + 8); edge(g, cx + wV, cy + 8, W, cy + 8); edge(g, 0, cy + wH - 8, cx, cy + wH - 8); edge(g, cx + wV, cy + wH - 8, W, cy + wH - 8);
    kerbV(g, cx, cy + wH, H); kerbV(g, cx + wV, cy + wH, H); edge(g, cx + 8, cy + wH, cx + 8, H); edge(g, cx + wV - 8, cy + wH, cx + wV - 8, H);
    if (r.kind === "cross") { kerbV(g, cx, 0, cy); kerbV(g, cx + wV, 0, cy); edge(g, cx + 8, 0, cx + 8, cy); edge(g, cx + wV - 8, 0, cx + wV - 8, cy); }
    else { kerbH(g, cy, cx, cx + wV); edge(g, cx, cy + 8, cx + wV, cy + 8); }
    // stop lines on the approach half of each arm (keep left)
    line(g, cx + 8, cy + wH, cx + ours * LANE - 4, cy + wH, 8, MARK);                      // us, from the bottom
    line(g, cx + wV, cy + 8, cx + wV, cy + onc * LANE - 4, 8, MARK);                        // from the right (their keep-left is the top half)
    line(g, cx, cy + onc * LANE + 4, cx, cy + wH - 8, 8, MARK);                            // from the left
    if (r.kind === "cross") line(g, cx + ours * LANE + 4, cy, cx + wV - 8, cy, 8, MARK);   // from the top
    for (let i = 0; i < ours; i++) roadArrow(g, cx + (i + 0.5) * LANE, Math.min(H - 80, cy + wH + 190), "ahead");
  } else if (r.kind === "roundabout") {
    const lanes = Math.max(1, Math.min(2, ours)), half = lanes * LANE, R = 150 + lanes * 50, rIsl = 80;
    const cx = 500, cy = 350;
    g.fillStyle = ASPHALT; g.fillRect(cx - half, 0, half * 2, H); g.fillRect(0, cy - half, W, half * 2);
    g.beginPath(); g.arc(cx, cy, R, 0, Math.PI * 2); g.fill();
    const yA = Math.sqrt(R * R - half * half);     // where the arm meets the circle
    kerbV(g, cx - half, 0, cy - yA); kerbV(g, cx + half, 0, cy - yA); kerbV(g, cx - half, cy + yA, H); kerbV(g, cx + half, cy + yA, H);
    kerbH(g, cy - half, 0, cx - yA); kerbH(g, cy + half, 0, cx - yA); kerbH(g, cy - half, cx + yA, W); kerbH(g, cy + half, cx + yA, W);
    g.fillStyle = VERGE; g.beginPath(); g.arc(cx, cy, rIsl, 0, Math.PI * 2); g.fill();
    g.strokeStyle = KERB; g.lineWidth = 8; g.beginPath(); g.arc(cx, cy, rIsl, 0, Math.PI * 2); g.stroke();
    g.save(); g.setLineDash([22, 18]); g.strokeStyle = MARK; g.lineWidth = 4;
    g.beginPath(); g.arc(cx, cy, R - 6, 0, Math.PI * 2); g.stroke();
    if (lanes === 2) { g.beginPath(); g.arc(cx, cy, rIsl + (R - rIsl) / 2, 0, Math.PI * 2); g.stroke(); }
    g.restore();
    doubleCentre(g, cx, 0, cx, cy - R); doubleCentre(g, cx, cy + R, cx, H); doubleCentre(g, 0, cy, cx - R, cy); doubleCentre(g, cx + R, cy, W, cy);
    if (lanes === 2) { laneDash(g, cx - LANE, 0, cx - LANE, cy - R); laneDash(g, cx + LANE, 0, cx + LANE, cy - R); laneDash(g, cx - LANE, cy + R, cx - LANE, H); laneDash(g, cx + LANE, cy + R, cx + LANE, H); laneDash(g, 0, cy - LANE, cx - R, cy - LANE); laneDash(g, 0, cy + LANE, cx - R, cy + LANE); laneDash(g, cx + R, cy - LANE, W, cy - LANE); laneDash(g, cx + R, cy + LANE, W, cy + LANE); }
    line(g, cx - half + 8, cy + yA, cx - 4, cy + yA, 6, MARK, [10, 8]);   // give way at our entry
  } else {
    g.fillStyle = "#ffffff"; g.fillRect(0, 0, W, H);
    g.save(); g.strokeStyle = "#e6e6e6"; g.lineWidth = 1;
    for (let x = 0; x <= W; x += 50) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x, H); g.stroke(); }
    for (let y = 0; y <= H; y += 50) { g.beginPath(); g.moveTo(0, y); g.lineTo(W, y); g.stroke(); }
    g.restore();
  }
  g.restore();
}

// top-view vehicle: body, roof, windscreen, headlights — nose UP at rot 0
function drawCar(g: CanvasRenderingContext2D, colour: string, scale = 1) {
  const w = 46 * scale, h = 92 * scale;
  g.save();
  g.shadowColor = "rgba(0,0,0,0.35)"; g.shadowBlur = 6; g.shadowOffsetY = 2;
  g.fillStyle = colour; roundRect(g, -w / 2, -h / 2, w, h, 10 * scale); g.fill();
  g.shadowColor = "transparent";
  g.strokeStyle = "rgba(0,0,0,0.55)"; g.lineWidth = 1.5; roundRect(g, -w / 2, -h / 2, w, h, 10 * scale); g.stroke();
  g.fillStyle = "rgba(20,30,40,0.75)";
  roundRect(g, -w / 2 + 6, -h / 2 + 18 * scale, w - 12, 14 * scale, 4); g.fill();
  roundRect(g, -w / 2 + 6, h / 2 - 24 * scale, w - 12, 10 * scale, 4); g.fill();
  g.fillStyle = "rgba(255,255,255,0.18)"; roundRect(g, -w / 2 + 6, -h / 2 + 34 * scale, w - 12, h - 60 * scale, 4); g.fill();
  g.fillStyle = "#fff8c2"; g.beginPath(); g.arc(-w / 2 + 9, -h / 2 + 7, 3.5, 0, Math.PI * 2); g.arc(w / 2 - 9, -h / 2 + 7, 3.5, 0, Math.PI * 2); g.fill();
  g.restore();
}
function drawTruck(g: CanvasRenderingContext2D, colour: string) {
  g.save();
  g.shadowColor = "rgba(0,0,0,0.35)"; g.shadowBlur = 6; g.shadowOffsetY = 2;
  g.fillStyle = "#e8e8e4"; roundRect(g, -28, -40, 56, 108, 6); g.fill();          // the box / tray
  g.fillStyle = colour; roundRect(g, -26, -72, 52, 34, 8); g.fill();             // the cab
  g.shadowColor = "transparent";
  g.strokeStyle = "rgba(0,0,0,0.55)"; g.lineWidth = 1.5; roundRect(g, -28, -40, 56, 108, 6); g.stroke(); roundRect(g, -26, -72, 52, 34, 8); g.stroke();
  g.fillStyle = "rgba(20,30,40,0.75)"; roundRect(g, -20, -58, 40, 12, 3); g.fill();
  g.fillStyle = "#fff8c2"; g.beginPath(); g.arc(-18, -66, 3.5, 0, Math.PI * 2); g.arc(18, -66, 3.5, 0, Math.PI * 2); g.fill();
  g.restore();
}
function drawBike(g: CanvasRenderingContext2D, colour: string) {
  g.save();
  g.shadowColor = "rgba(0,0,0,0.35)"; g.shadowBlur = 5; g.shadowOffsetY = 2;
  g.fillStyle = "#222"; roundRect(g, -5, -34, 10, 20, 4); g.fill(); roundRect(g, -5, 14, 10, 20, 4); g.fill();
  g.fillStyle = colour; roundRect(g, -9, -18, 18, 36, 6); g.fill();
  g.shadowColor = "transparent";
  g.fillStyle = "#333"; g.fillRect(-16, -12, 32, 4);
  g.fillStyle = "#f1d7b8"; g.beginPath(); g.arc(0, 2, 7, 0, Math.PI * 2); g.fill();
  g.strokeStyle = "rgba(0,0,0,0.5)"; g.lineWidth = 1; g.beginPath(); g.arc(0, 2, 7, 0, Math.PI * 2); g.stroke();
  g.fillStyle = "#fff8c2"; g.beginPath(); g.arc(0, -30, 3, 0, Math.PI * 2); g.fill();
  g.restore();
}
function drawPedestrian(g: CanvasRenderingContext2D) {
  g.save();
  g.shadowColor = "rgba(0,0,0,0.35)"; g.shadowBlur = 4; g.shadowOffsetY = 2;
  g.fillStyle = "#2d7a3a"; g.beginPath(); g.ellipse(0, 0, 20, 13, 0, 0, Math.PI * 2); g.fill();    // shoulders
  g.shadowColor = "transparent";
  g.strokeStyle = "#fff"; g.lineWidth = 2; g.beginPath(); g.ellipse(0, 0, 20, 13, 0, 0, Math.PI * 2); g.stroke();
  g.fillStyle = "#f1d7b8"; g.beginPath(); g.arc(0, 0, 9, 0, Math.PI * 2); g.fill();              // head
  g.strokeStyle = "rgba(0,0,0,0.5)"; g.lineWidth = 1; g.beginPath(); g.arc(0, 0, 9, 0, Math.PI * 2); g.stroke();
  g.restore();
}
function drawImpact(g: CanvasRenderingContext2D) {
  g.save();
  g.fillStyle = RED; g.strokeStyle = "#fff"; g.lineWidth = 2;
  g.beginPath();
  for (let i = 0; i < 16; i++) { const a = (i * Math.PI) / 8; const rr = i % 2 ? 14 : 30; g.lineTo(Math.cos(a) * rr, Math.sin(a) * rr); }
  g.closePath(); g.fill(); g.stroke();
  g.fillStyle = "#fff"; g.font = "bold 9px sans-serif"; g.textAlign = "center"; g.textBaseline = "middle"; g.fillText("HIT", 0, 0);
  g.restore();
}
function haloText(g: CanvasRenderingContext2D, text: string, x: number, y: number, size = 20) {
  g.save(); g.font = `bold ${size}px sans-serif`; g.textAlign = "left"; g.textBaseline = "middle";
  const w = g.measureText(text).width;
  g.fillStyle = "rgba(255,255,255,0.92)"; roundRect(g, x - 7, y - size * 0.75, w + 14, size * 1.5, 5); g.fill();
  g.strokeStyle = "rgba(0,0,0,0.25)"; g.lineWidth = 1; g.stroke();
  g.fillStyle = "#111"; g.fillText(text, x, y); g.restore();
  return w;
}
function drawLine(g: CanvasRenderingContext2D, it: Extract<SketchItem, { kind: "line" }>, dark = false) {
  const mark = dark ? "#3a3a3a" : MARK, kerb = dark ? "#8a8578" : KERB;
  if (it.type === "dash") line(g, it.x1, it.y1, it.x2, it.y2, 4, mark, [30, 24]);
  else if (it.type === "solid") line(g, it.x1, it.y1, it.x2, it.y2, 4, mark);
  else if (it.type === "kerb") line(g, it.x1, it.y1, it.x2, it.y2, 8, kerb);
  else if (it.type === "double") {
    const ang = Math.atan2(it.y2 - it.y1, it.x2 - it.x1), nx = -Math.sin(ang) * 4, ny = Math.cos(ang) * 4;
    line(g, it.x1 + nx, it.y1 + ny, it.x2 + nx, it.y2 + ny, 3, mark); line(g, it.x1 - nx, it.y1 - ny, it.x2 - nx, it.y2 - ny, 3, mark);
  } else { line(g, it.x1, it.y1, it.x2, it.y2, 10, "rgba(20,20,20,0.75)"); line(g, it.x1, it.y1, it.x2, it.y2, 4, "rgba(0,0,0,0.9)", [6, 6]); }
}

function drawItem(g: CanvasRenderingContext2D, it: SketchItem, selected: boolean, dark = false) {
  g.save();
  if (it.kind === "stamp") {
    g.translate(it.x, it.y); g.rotate((it.rot * Math.PI) / 180);
    if (it.stamp === "impact") drawImpact(g);
    else if (it.stamp === "bike") drawBike(g, BLUE);
    else if (it.stamp === "truck") drawTruck(g, BLUE);
    else if (it.stamp === "pedestrian") drawPedestrian(g);
    else drawCar(g, it.stamp === "coretel" ? ORANGE : BLUE);
    if (selected) { g.strokeStyle = "#fff"; g.setLineDash([6, 4]); g.lineWidth = 2; g.strokeRect(-36, it.stamp === "truck" ? -80 : -54, 72, it.stamp === "truck" ? 156 : 108); }
  } else if (it.kind === "arrow") {
    const ang = Math.atan2(it.y2 - it.y1, it.x2 - it.x1);
    const head = (fill: string, lw: number) => {
      g.strokeStyle = fill; g.fillStyle = fill; g.lineWidth = lw; g.lineCap = "round"; g.lineJoin = "round";
      g.beginPath(); g.moveTo(it.x1, it.y1); g.lineTo(it.x2, it.y2); g.stroke();
      g.beginPath(); g.moveTo(it.x2 + 6 * Math.cos(ang), it.y2 + 6 * Math.sin(ang));
      g.lineTo(it.x2 - 20 * Math.cos(ang - 0.5), it.y2 - 20 * Math.sin(ang - 0.5));
      g.lineTo(it.x2 - 20 * Math.cos(ang + 0.5), it.y2 - 20 * Math.sin(ang + 0.5)); g.closePath(); g.fill(); g.stroke();
    };
    head("#ffffff", 10); head("#1a1a1a", 5);
    if (selected) { g.setLineDash([6, 4]); g.lineWidth = 2; g.strokeStyle = "#fff"; g.strokeRect(Math.min(it.x1, it.x2) - 12, Math.min(it.y1, it.y2) - 12, Math.abs(it.x2 - it.x1) + 24, Math.abs(it.y2 - it.y1) + 24); }
  } else if (it.kind === "line") {
    drawLine(g, it, dark);
    if (selected) { g.setLineDash([6, 4]); g.lineWidth = 2; g.strokeStyle = "#fff"; g.strokeRect(Math.min(it.x1, it.x2) - 12, Math.min(it.y1, it.y2) - 12, Math.abs(it.x2 - it.x1) + 24, Math.abs(it.y2 - it.y1) + 24); }
  } else if (it.kind === "pen") {
    const stroke = (c: string, lw: number) => { g.strokeStyle = c; g.lineWidth = lw; g.lineCap = "round"; g.lineJoin = "round"; g.beginPath(); it.pts.forEach(([x, y], i) => i ? g.lineTo(x, y) : g.moveTo(x, y)); g.stroke(); };
    stroke("#ffffff", 8); stroke(RED, 4);
  } else if (it.kind === "area") {
    g.fillStyle = "rgba(224,50,58,0.22)"; g.fillRect(it.x, it.y, it.w, it.h);
    g.save(); g.beginPath(); g.rect(it.x, it.y, it.w, it.h); g.clip();
    g.strokeStyle = "rgba(224,50,58,0.55)"; g.lineWidth = 2;
    for (let d = -it.h; d < it.w; d += 14) { g.beginPath(); g.moveTo(it.x + d, it.y); g.lineTo(it.x + d + it.h, it.y + it.h); g.stroke(); }
    g.restore();
    g.strokeStyle = RED; g.lineWidth = 3; g.setLineDash(selected ? [6, 4] : []); g.strokeRect(it.x, it.y, it.w, it.h);
  } else {
    const w = haloText(g, it.text, it.x, it.y);
    if (selected) { g.setLineDash([6, 4]); g.lineWidth = 2; g.strokeStyle = "#111"; g.strokeRect(it.x - 10, it.y - 18, w + 20, 36); }
  }
  g.restore();
}
function drawLegend(g: CanvasRenderingContext2D, items: SketchItem[]) {
  const used = new Set<string>(items.map(i => i.kind === "stamp" ? i.stamp : i.kind));
  const entries: Array<{ w: number; draw: (x: number, y: number) => void }> = [];
  g.save(); g.font = "12px sans-serif"; g.textBaseline = "middle"; g.textAlign = "left";
  const stamp = (key: SketchStamp, label: string, fn: (gg: CanvasRenderingContext2D) => void, sc: number, pad: number) => {
    if (!used.has(key)) return;
    entries.push({ w: pad + g.measureText(label).width + 18, draw: (x, y) => { g.save(); g.translate(x + pad / 2, y); g.scale(sc, sc); fn(g); g.restore(); g.fillStyle = "#111"; g.fillText(label, x + pad, y); } });
  };
  stamp("coretel", "Coretel vehicle", gg => drawCar(gg, ORANGE), 0.36, 28);
  stamp("other", "Other vehicle", gg => drawCar(gg, BLUE), 0.36, 28);
  stamp("truck", "Truck", gg => drawTruck(gg, BLUE), 0.3, 28);
  stamp("bike", "Bike", gg => drawBike(gg, BLUE), 0.5, 26);
  stamp("pedestrian", "Person", gg => drawPedestrian(gg), 0.7, 32);
  stamp("impact", "Impact", gg => drawImpact(gg), 0.45, 32);
  if (used.has("area")) entries.push({ w: 28 + g.measureText("Debris / impact area").width + 18, draw: (x, y) => { g.fillStyle = "rgba(224,50,58,0.25)"; g.fillRect(x + 2, y - 8, 20, 16); g.strokeStyle = RED; g.lineWidth = 2; g.strokeRect(x + 2, y - 8, 20, 16); g.fillStyle = "#111"; g.fillText("Debris / impact area", x + 28, y); } });
  if (entries.length === 0) { g.restore(); return; }
  const total = entries.reduce((a, e) => a + e.w, 0) + 12;
  const x0 = W - total - 10, y0 = H - 54;
  g.fillStyle = "rgba(255,255,255,0.94)"; roundRect(g, x0, y0, total, 44, 6); g.fill();
  g.strokeStyle = "rgba(0,0,0,0.2)"; g.lineWidth = 1; g.stroke();
  let x = x0 + 6;
  for (const e of entries) { e.draw(x, y0 + 22); x += e.w; }
  g.restore();
}
function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath(); g.moveTo(x + r, y); g.lineTo(x + w - r, y); g.quadraticCurveTo(x + w, y, x + w, y + r);
  g.lineTo(x + w, y + h - r); g.quadraticCurveTo(x + w, y + h, x + w - r, y + h); g.lineTo(x + r, y + h);
  g.quadraticCurveTo(x, y + h, x, y + h - r); g.lineTo(x, y + r); g.quadraticCurveTo(x, y, x + r, y); g.closePath();
}

// -----------------------------------------------------------------------------
// geometry helpers
// -----------------------------------------------------------------------------
/** Snap a dragged line to the nearest 45° when it is within ~10° of one. */
function snap(x1: number, y1: number, x: number, y: number) {
  const dx = x - x1, dy = y - y1, len = Math.hypot(dx, dy);
  if (len < 1) return { x, y };
  const ang = Math.atan2(dy, dx), step = Math.PI / 4, near = Math.round(ang / step) * step;
  if (Math.abs(ang - near) > (10 * Math.PI) / 180) return { x, y };
  return { x: x1 + Math.cos(near) * len, y: y1 + Math.sin(near) * len };
}
function normArea(a: Extract<SketchItem, { kind: "area" }>): SketchItem {
  return { kind: "area", x: Math.min(a.x, a.x + a.w), y: Math.min(a.y, a.y + a.h), w: Math.abs(a.w), h: Math.abs(a.h) };
}
function origin(it: SketchItem): [number, number] {
  if (it.kind === "stamp" || it.kind === "text" || it.kind === "area") return [it.x, it.y];
  if (it.kind === "arrow" || it.kind === "line") return [it.x1, it.y1];
  return it.pts[0];
}
function hitTest(items: SketchItem[], x: number, y: number): number | null {
  // stamps and labels first (they sit on top), then lines, then areas (big, underneath)
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    if (it.kind === "stamp" && Math.abs(it.x - x) < 40 && Math.abs(it.y - y) < (it.stamp === "truck" ? 80 : 56)) return i;
    if (it.kind === "text" && x >= it.x - 8 && x <= it.x + 8 + it.text.length * 11 && Math.abs(it.y - y) < 18) return i;
  }
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    if ((it.kind === "arrow" || it.kind === "line") && distToSeg(x, y, it.x1, it.y1, it.x2, it.y2) < 16) return i;
    if (it.kind === "pen" && it.pts.some(([px, py]) => Math.hypot(px - x, py - y) < 14)) return i;
  }
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    if (it.kind === "area" && x >= it.x && x <= it.x + it.w && y >= it.y && y <= it.y + it.h) return i;
  }
  return null;
}
function distToSeg(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1; const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / l2)) : 0;
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}
function moveTo(it: SketchItem, x: number, y: number): SketchItem {
  if (it.kind === "stamp" || it.kind === "text" || it.kind === "area") return { ...it, x, y };
  if (it.kind === "arrow" || it.kind === "line") { const dx = x - it.x1, dy = y - it.y1; return { ...it, x1: x, y1: y, x2: it.x2 + dx, y2: it.y2 + dy }; }
  const dx = x - it.pts[0][0], dy = y - it.pts[0][1];
  return { ...it, pts: it.pts.map(([px, py]) => [px + dx, py + dy] as [number, number]) };
}
